'use client';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { ReactNode, useCallback, useState } from 'react';
import { Reply } from 'lucide-react';

interface SwipeableItemProps {
  children: ReactNode;
  onSwipeAction?: () => void;
  swipeThreshold?: number;
  iconThreshold?: number;
  disabled?: boolean;
  icon?: ReactNode;
  iconClassName?: string;
}

export const SwipeableItem = ({
  children,
  onSwipeAction,
  swipeThreshold = 60,
  iconThreshold = 20,
  disabled = false,
  icon,
  iconClassName = 'absolute left-2 z-30 flex items-center justify-center w-10 h-10 bg-blue-500 rounded-full',
}: SwipeableItemProps) => {
  const x = useMotionValue(0);
  const opacity = useTransform(x, [0, swipeThreshold, swipeThreshold + 40], [0, 0.5, 1]);
  const [showIcon, setShowIcon] = useState(false);

  const handleDragEnd = useCallback(
    async (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (onSwipeAction && Math.abs(info.offset.x) > swipeThreshold) {
        onSwipeAction();
      }
      setShowIcon(false);
    },
    [onSwipeAction, swipeThreshold],
  );

  const handleDrag = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (info.offset.x > iconThreshold) {
        setShowIcon(true);
      } else {
        setShowIcon(false);
      }
    },
    [iconThreshold],
  );

  if (disabled || !onSwipeAction) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      {showIcon && (
        <motion.div style={{ opacity }} className={iconClassName}>
          {icon || <Reply className="h-5 w-5 text-white" />}
        </motion.div>
      )}

      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 100 }}
        dragElastic={0.2}
        dragSnapToOrigin
        style={{ x }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}>
        {children}
      </motion.div>
    </div>
  );
};
